import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { languages } from '../languages';
import { mcDePageClass } from '../utils/branding';

const normalizeText = (text) => {
  if (!text) return text;
  const lower = text.toLowerCase();
  return lower.replace(/(?:^|\.\s*)([a-z])/g, (_, c) => c.toUpperCase());
};

const formatValue = (val) => {
  const s = String(val ?? '');
  if (s.includes('.')) {
    const [intPart, decPart] = s.split('.');
    return `${intPart}.${decPart.slice(0, 3)}`;
  }
  return s;
};

export default function BarcodeScanPage() {

  const { language } = useLanguage();

  let translationLanguage = language;
  if (language === 'befr' || language === 'be-fr') translationLanguage = 'french';
  if (language === 'benl' || language === 'be-nl') translationLanguage = 'nederlands';
  if (language === 'bh-ar' || language === 'bahrain-arabic') translationLanguage = 'arabic';
  if (language === 'bh-en' || language === 'bahrain-english') translationLanguage = 'english';
  if (language === 'uae-ar' || language === 'uae-arabic') translationLanguage = 'arabic';
  if (language === 'uae-en' || language === 'uae-english') translationLanguage = 'english';
  if (language === 'sa-ar' || language === 'sa-arabic') translationLanguage = 'arabic';
  if (language === 'sa-en' || language === 'sa-english') translationLanguage = 'english';
  if (language === 'kw-ar' || language === 'kw-arabic') translationLanguage = 'arabic';
  if (language === 'kw-en' || language === 'kw-english') translationLanguage = 'english';
  if (language === 'qa-ar' || language === 'qa-arabic') translationLanguage = 'arabic';
  if (language === 'qa-en' || language === 'qa-english') translationLanguage = 'english';
  if (language === 'om-ar' || language === 'om-arabic') translationLanguage = 'arabic';
  if (language === 'om-en' || language === 'om-english') translationLanguage = 'english';
  if (language === 'iq-ar' || language === 'iq-arabic') translationLanguage = 'arabic';
  if (language === 'iq-en' || language === 'iq-english') translationLanguage = 'english';
  if (language === 'jo-ar' || language === 'jo-arabic') translationLanguage = 'arabic';
  if (language === 'jo-en' || language === 'jo-english') translationLanguage = 'english';
  if (language === 'lb-ar' || language === 'lb-arabic') translationLanguage = 'arabic';
  if (language === 'lb-en' || language === 'lb-english') translationLanguage = 'english';
  if (language === 'sy-ar' || language === 'sy-arabic') translationLanguage = 'arabic';
  if (language === 'sy-en' || language === 'sy-english') translationLanguage = 'english';
  if (language === 'ps-ar' || language === 'ps-arabic') translationLanguage = 'arabic';
  if (language === 'ps-en' || language === 'ps-english') translationLanguage = 'english';
  if (language === 'ye-ar' || language === 'ye-arabic') translationLanguage = 'arabic';
  if (language === 'ye-en' || language === 'ye-english') translationLanguage = 'english';
  if (language === 'eg-ar' || language === 'eg-arabic') translationLanguage = 'arabic';
  if (language === 'eg-en' || language === 'eg-english') translationLanguage = 'english';
  if (language === 'pl-pl') translationLanguage = 'polish';
    if (language === 'cyprus' || language === 'cy' || language === 'el-cy') translationLanguage = 'greek';

  const t = languages[translationLanguage] || languages['english'];

  const videoRef    = useRef(null);
  const codeReader  = useRef(null);
  const controlsRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currency, setCurrency] = useState(() => (t.countryCurrency) || 'EUR');
  const [prices, setPrices] = useState(null);
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);
  const [pricesError, setPricesError] = useState(null);
  const [barcode, setBarcode] = useState(null);

  useEffect(() => {
    codeReader.current = new BrowserMultiFormatReader();
    startScanner();
    return () => controlsRef.current?.stop();
  }, []);

  useEffect(() => {
    if (!result) return;

    if (!barcode) return;

    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      setIsLoadingPrices(true);
      setPrices(null);
      setPricesError(null);
      try {
        const res = await axios.post('/api/productPrices', { barcode, currency }, {signal: signal});
        setPrices(res.data);
      } catch (err) {
        console.error('Product prices fetch failed:', err);
        if (err.name !== 'AbortError') setPricesError(t.unableToFetchPrices + ': ' + currency);
      } finally {
        setIsLoadingPrices(false);
      }
    })();

    return () => controller.abort();
  }, [result, currency]);


const startScanner = async () => {
  try {
    controlsRef.current?.stop();
    controlsRef.current = null;

    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }

    codeReader.current = new BrowserMultiFormatReader();

    setResult(null);
    setError(null);
    setScanning(true);

    const devices = await BrowserMultiFormatReader.listVideoInputDevices();

    if (!devices.length) {
      throw new Error('No camera devices found');
    }

    const backCamera = devices.find(d =>
          /back|rear|environment/gi.test(d.label)
        );
    const selectedDeviceId = backCamera ? backCamera.deviceId : devices[0].deviceId;

    codeReader.current.decodeFromVideoDevice(selectedDeviceId, videoRef.current, (decoded, err, controls) => {
      controlsRef.current = controls;
      if (decoded) {
        controls.stop();
        setScanning(false);
        if(decoded.getText())
            setBarcode(decoded.getText());
        fetchProductInfo(decoded.getText(), language);
        if(result)
            controls.stop();
            setScanning(false);
      }
    });

  } catch (err) {
    console.error('Camera start failed:', err);
    setError(t.cameraFailedStartError || 'Camera failed to start');
    setScanning(false);
  }
};


  const fetchProductInfo = async (barcode, language) => {
    try {
      setIsLoading(true);
      const response = await axios.post('/api/barcodeLookup', { barcode, language });
      const product  = response.data;
      if (product && product.product_name) {
        setError(null);
        setResult(product);
      } else {
        setResult(null);
        setError(t.productNotFoundError || 'Product not found');
      }
    } catch (err) {
      console.error('Product Lookup failed:', err);
      setResult(null);
      setError(t.productLookupFailed || 'Product Lookup failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={mcDePageClass(language)} style={{ textAlign: 'center' }}>
      {!result && (
              <h1>📸 {t.scanYourProductBarcode}</h1>
      )}

      {!result && (
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '400px',
          margin: '1rem auto',
          overflow: 'hidden',
          border: '2px solid #444',
          borderRadius: '8px'
        }}>
          <video ref={videoRef} style={{ width: '100%', height: 'auto' }} muted />
          {scanning && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '3px',
              background: 'rgba(255, 0, 0, 0.8)',
              animation: 'scan 2s infinite'
            }} />
          )}
        {isLoading && (
          <div className="loader-overlay">
            <div className="loader" />
          </div>
        )}
        </div>
      )}

      {error && <div style={{ color: '#c00', marginBottom: '1rem' }}>{error}</div>}

      {result && (
        <div style={{
          maxWidth: '400px',
          margin: '1rem auto',
          padding: '1rem',
          border: '1px solid #ddd',
          borderRadius: '8px',
          background: '#fff'
        }}>
        {result.story && (
                    <div style={{ padding: '0.75rem', borderRadius: '4px', textAlign: 'left' }}>
                      <p style={{ margin: '0.5rem 0 0', lineHeight: '1.4' }}>{result.story}</p>
                    </div>
                  )}
        </div>
      )}

      {result && (
        <div style={{
          maxWidth: '400px',
          margin: '1rem auto',
          padding: '1rem',
          border: '1px solid #ddd',
          borderRadius: '8px',
          background: '#fff'
        }}>
          {result.image_front_url && (
            <img
              src={result.image_front_url}
              alt={normalizeText(result.product_name) || 'Product Image'}
              style={{ width: '100%', borderRadius: '4px', objectFit: 'cover' }}
            />
          )}
          <h2 style={{ margin: '0.5rem 0' }}>{normalizeText(result.product_name) || 'Unnamed product'}</h2>
          {result.brands && <p><strong>{t.productBrand}:</strong> {normalizeText(result.brands)}</p>}

          {result.nutriscore_grade && (
            <p>
              <strong>{t.productNutriScore}:</strong>{' '}
              <span style={{
                fontWeight: 'bold',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                color: 'white',
                background: {
                  A: '#4caf50',
                  B: '#8bc34a',
                  C: '#ffc107',
                  D: '#ff9800',
                  E: '#f44336'
                }[result.nutriscore_grade.toUpperCase()] || '#ccc'
              }}>{result.nutriscore_grade.toUpperCase()}</span>
            </p>
          )}

          {result.ingredients_text && (
            <p>
              <strong>{t.productIngredients}:</strong>{' '}
              {normalizeText(result.ingredients_text)}
            </p>
          )}

          {result.nutriments && (
            <div style={{ marginTop: '1rem' }}>
              <strong>{t.productNutritionalValuesPer100g}:</strong>{' '}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '0.5rem', textAlign: 'left' }}>{t.productNutrient}</th>
                    <th style={{ borderBottom: '1px solid #ccc', padding: '0.5rem' }}>{t.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.nutriments['energy-kcal_100g'] != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.energy}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments['energy-kcal_100g'])} kcal</td>
                    </tr>
                  )}
                  {result.nutriments.fat_100g != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.fat}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments.fat_100g)} g</td>
                    </tr>
                  )}
                  {result.nutriments['saturated-fat_100g'] != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.saturatedFat}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments['saturated-fat_100g'])} g</td>
                    </tr>
                  )}
                  {result.nutriments.sugars_100g != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.sugars}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments.sugars_100g)} g</td>
                    </tr>
                  )}
                  {result.nutriments['added-sugars_100g'] != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.addedSugars}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments['added-sugars_100g'])} g</td>
                    </tr>
                  )}
                  {result.nutriments.fiber_100g != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.fiber}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments.fiber_100g)} g</td>
                    </tr>
                  )}
                  {result.nutriments.proteins_100g != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.protein}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments.proteins_100g)} g</td>
                    </tr>
                  )}
                  {result.nutriments.salt_100g != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.salt}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments.salt_100g)} g</td>
                    </tr>
                  )}
                  {result.nutriments['vitamin-d_100g'] != null && (
                    <tr>
                      <td style={{ padding: '0.5rem' }}>{t.vitaminD}</td>
                      <td style={{ padding: '0.5rem' }}>{formatValue(result.nutriments['vitamin-d_100g'])} g</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {result && (
        <div style={{
          maxWidth: '420px',
          margin: '1rem auto',
          padding: '1rem',
          border: '1px solid #e6e9ee',
          borderRadius: '12px',
          background: '#ffffff',
          boxShadow: '0 6px 18px rgba(20,30,60,0.04)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <strong style={{ fontSize: '1.05rem', color: '#111' }}>{t.latestMarketPrices}</strong>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label htmlFor="currency-select" style={{ fontSize: '0.85rem', color: '#555' }}>{t.currency}</label>

              <div style={{ position: 'relative' }}>
                <select
                  id="currency-select"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    padding: '0.45rem 1.25rem 0.45rem 0.6rem',
                    borderRadius: '8px',
                    border: '1px solid #dfe6f0',
                    background: '#fbfdff',
                    color: '#111',
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    minWidth: '110px'
                  }}
                >
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="SEK">SEK</option>
                  <option value="RSD">RSD</option>
                  <option value="PLN">PLN</option>
                  <option value="NOK">NOK</option>
                  <option value="CHF">CHF</option>
                  <option value="BHD">BHD</option>
                  <option value="SGD">SGD</option>
                </select>

                <span style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#8b94a8',
                  fontSize: '0.85rem'
                }}>▾</span>
              </div>
            </div>
          </div>

          {isLoadingPrices && (
            <div style={{ padding: '0.6rem 0', textAlign: 'center', color: '#666' }}>{t.loadingPrices}…</div>
          )}

          {pricesError && (
            <div style={{ color: '#abaaa9', padding: '0.5rem 0' }}>{pricesError}</div>
          )}

          {Array.isArray(prices) && prices.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '0.6rem' }}>
              {prices.map((p, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0.65rem',
                    borderRadius: '8px',
                    background: '#fbfbff',
                    border: '1px solid #f0f3fa'
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: '#0b1220' }}>
                      {p.price != null ? `${p.price} ${p.currency || currency}` : '—'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                      {p.date ? new Date(p.date).toLocaleDateString('nl-NL') : ''}
                    </div>
                  </div>

                  <div style={{
                    fontSize: '0.82rem',
                    color: '#25304a',
                    padding: '0.28rem 0.6rem',
                    borderRadius: '6px',
                    background: '#fff',
                    border: '1px solid #edf0f6'
                  }}>
                    {p.currency || currency}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            prices && <div style={{ padding: '0.6rem 0', color: '#666' }}>No prices available.</div>
          )}
        </div>
      )}

      <button
        onClick={startScanner}
        disabled={scanning}
        style={{
          display: 'block',
          margin: '1rem auto',
          marginBottom: '2rem',
          padding: '0.5rem 1rem',
          border: 'none',
          background: '#4caf50',
          color: 'white',
          borderRadius: '4px',
          cursor: scanning ? 'default' : 'pointer',
          opacity: scanning ? 0.6 : 1
        }}
      >
        {scanning ? t.scanning+'…' : result ? t.scanAnother : t.startScan}
      </button>

      <style>{`
        @keyframes scan {
          0%   { top: 0; }
          50%  { top: calc(100% - 3px); }
          100% { top: 0; }
        }
       .loader-overlay {
         position: absolute;
         top: 0;
         left: 0;
         width: 100%;
         height: 100%;
         display: flex;
         align-items: center;
         justify-content: center;
         background: rgba(255, 255, 255, 0.7);
       }
       .loader {
         border: 4px solid #f3f3f3;
         border-top: 4px solid #4caf50;
         border-radius: 50%;
         width: 40px;
         height: 40px;
         animation: spin 1s linear infinite;
       }
       @keyframes spin {
         0% { transform: rotate(0deg); }
         100% { transform: rotate(360deg); }
       }
      `}</style>
    </div>
  );
}
